import React from "react"
import { Control, Controller } from "react-hook-form"
import { FieldErrors } from "react-hook-form/dist/types/errors"
import { UseFormSetValue } from "react-hook-form/dist/types/form"
import { Common } from "lib/types"
import { BsgGiftCardConfig, InputBsgGiftCardConfig } from "lib/types/generated/graphql-types"

import { SidebarDivider } from "../../DashboardSales/Shared.styled"

import Input from "../../Ui/Form/Input"
import SingleSelect from "../../Ui/Form/SingleSelect"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackInput from "../../Ui/Form/FallbackInput"

export interface ExtendedInputCustomGiftCardConfig extends InputBsgGiftCardConfig {
  id: string
}

type Props = {
  control: Control<ExtendedInputCustomGiftCardConfig>
  errors: FieldErrors
  setValue: UseFormSetValue<ExtendedInputCustomGiftCardConfig>
  showId?: boolean
  defaultValues?: BsgGiftCardConfig
}

const scopeOptions: Common.Option[] = [
  {
    value: "brink/gift-card",
    label: "brink/gift-card"
  }
]

const CustomGiftCardConfigInputs = ({
  control,
  errors,
  setValue,
  showId,
  defaultValues
}: Props) => {
  const isValidHttpUrl = (string: string) => {
    try {
      const newUrl = new URL(string)
      return newUrl.protocol === "https:"
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const updateScopes = (selected: Common.Option) => {
    setValue("scopes", [selected.value])
  }

  return (
    <>
      {showId && (
        <>
          <Controller
            name="id"
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label="Identifier *"
                description="For internal use in Brink"
                placeholder="Aa - Zz, 0 - 9, -_."
                errors={errors}
              />
            )}
            control={control}
            rules={{
              required: "This is a required field",
              pattern: {
                value: /^[A-Za-z0-9_.-]+$/,
                message: "Only characters Aa-Zz and 0-9 are allowed. No spaces"
              }
            }}
            defaultValue=""
          />
          <SidebarDivider />
        </>
      )}
      <WithAuth
        requiredGroups={PermissionGroups.providers.write}
        fallback={<FallbackInput label={"Base URL"} value={defaultValues?.baseUrl} />}
      >
        <Controller
          name="baseUrl"
          render={({ field }) => (
            <Input {...field} type="text" label="Base URL *" errors={errors} />
          )}
          control={control}
          rules={{
            validate: {
              url: (value) => {
                if (!isValidHttpUrl(value)) return "Must be https format"
              }
            },
            required: "This is a required field"
          }}
          defaultValue={defaultValues?.baseUrl ?? ""}
        />
      </WithAuth>
      <WithAuth
        requiredGroups={PermissionGroups.providers.write}
        fallback={<FallbackInput label={"Token URL"} value={defaultValues?.tokenUrl} />}
      >
        <Controller
          name="tokenUrl"
          render={({ field }) => (
            <Input {...field} type="text" label="Token URL *" errors={errors} />
          )}
          control={control}
          rules={{
            validate: {
              url: (value) => {
                if (!isValidHttpUrl(value)) return "Must be https format"
              }
            },
            required: "This is a required field"
          }}
          defaultValue={defaultValues?.tokenUrl ?? ""}
        />
      </WithAuth>
      <WithAuth
        requiredGroups={PermissionGroups.providers.write}
        fallback={
          <FallbackInput label={"Scopes"} value={defaultValues?.scopes?.join(", ") ?? ""} />
        }
      >
        <Controller
          name="scopes"
          render={({ field }) => (
            <SingleSelect
              {...field}
              options={scopeOptions}
              setValue={updateScopes}
              defaultValue={{
                label:
                  scopeOptions.find((option) => defaultValues?.scopes?.includes(option.value))
                    ?.label ?? "Select scope",
                value: defaultValues?.scopes?.[0] ?? ""
              }}
              label="Scopes *"
            />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
      </WithAuth>
      <WithAuth requiredGroups={PermissionGroups.providers.write}>
        <Controller
          name="clientId"
          render={({ field }) => (
            <Input {...field} type="text" errors={errors} label="Client ID *" />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
          defaultValue=""
        />
        <Controller
          name="clientSecret"
          render={({ field }) => (
            <Input {...field} type="text" errors={errors} label="Client secret *" />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
          defaultValue=""
        />
      </WithAuth>
    </>
  )
}

export default CustomGiftCardConfigInputs
